import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { numberAbbreviator } from '@shared/utilities/numberFormatter';
import { Chart } from 'chart.js';
import { CHARTTYPES } from '@shared/constants/chart-types';
import { MONTHS } from '@shared/constants/months';
import { CustomChartService } from '@shared/services/custom-chart/custom-chart.service';

@Component({
  selector: 'custom-chart',
  templateUrl: './custom-chart.component.html',
  styleUrls: ['./custom-chart.component.scss']
})
export class CustomChartComponent implements OnInit {
  chart;
  rawData;
  chatSettings;
  growthAverage;

  @Input()
  ChartLegend: boolean = false;

  @Input()
  set ChartData(data) {
    this.rawData = data || [];
    this.growthAverage = data && data.length && data[0].averageGrowth;
  }

  @Input()
  set ChartTypeId(id) {
    const canvasReference = this.elementRef.nativeElement
      .querySelector('#canvasRef')
      .getContext('2d');
    const chartSettings = this.settingChart(id, this.rawData, canvasReference);
    this.initChart(canvasReference, chartSettings, id);
  }

  constructor(
    private elementRef: ElementRef,
    private _customChartService: CustomChartService
  ) {
    this._customChartService.updatePorfolioGraphValues.subscribe(data => {
      this.rawData = data || [];
      this.growthAverage = data && data.length && data[0].averageGrowth;
      const id = 2;
      const canvasReference = this.elementRef.nativeElement
        .querySelector('#canvasRef')
        .getContext('2d');
      const chartSettings = this.settingChart(
        id,
        this.rawData,
        canvasReference
      );
      this.initChart(canvasReference, chartSettings, id);
    });
  }

  ngOnInit() {}

  initChart(canvas, settings, id) {
    this.chart = new Chart(canvas, settings);
    if (CHARTTYPES.PORTFOLIO_VALUE_MOBILE === id) {
      const lineChart = this.chart.getDatasetMeta(0);
      lineChart.data.map((point, i) => {
        if (i !== 0 && i !== lineChart.data.length - 1) {
          point._model.radius = 0;
        }
      });
    }
  }

  settingChart(id, data, canvas) {
    const settings: { [key: string]: any } = {};

    if (CHARTTYPES.CAPITAL_GROWTH === id) {
      const chartLabel = data.map(i => i.year);
      const chartData = data.map(i => i.value);

      settings.type = 'line';
      settings.data = {};
      settings.data.labels = chartLabel;
      settings.data.datasets = [];
      settings.data.datasets.push({
        borderColor: '#F8D053',
        backgroundColor: 'rgba(254, 246, 221, 1)',
        data: chartData,
        fill: true
      });
      settings.options = {};
      settings.options.tooltips = {};
      settings.options.tooltips = {
        enabled: true,
        mode: 'single',
        callbacks: {}
      };
      settings.options.tooltips.callbacks = {
        label: function(item) {
          return `$${Math.ceil(item.yLabel).toLocaleString()}`;
        },
        title: function(item) {
          return `Year ${item.length > 0 && item[0].xLabel}`;
        }
      };
      settings.options.legend = {};
      settings.options.legend.display = false;
      settings.options.elements = {};
      settings.options.elements.point = {};
      settings.options.elements.point.borderWidth = 2;
      settings.options.elements.line = {};
      settings.options.elements.line.borderWidth = 2;
      settings.options.scales = {};
      settings.options.scales.xAxes = [];
      settings.options.scales.xAxes.push({
        display: true,
        gridLines: {
          display: false
        },
        ticks: {
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontStyle: 'normal',
          fontSize: 10
        }
      });
      settings.options.scales.yAxes = [];
      settings.options.scales.yAxes.push({
        display: true,
        position: 'right',
        gridLines: {
          borderDash: [4],
          drawBorder: false,
          drawTicks: false
        },
        ticks: {
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontSize: 10,
          padding: 10,
          beginAtZero: true,
          callback: function(value) {
            if (Number.isInteger(value)) {
              return `$${numberAbbreviator(value)}`;
            }
          }
        }
      });
    } else if (CHARTTYPES.SUBURB_PEFORMANCE === id) {
      const chartLabel = data.map(i => MONTHS[`${i.month}`]);
      const chartDataLine = data.map(i => i.averageRate);
      const chartDataBar = data.map(i => i.occupancy);
      let totalRate = 0;
      data.forEach(o => {
        totalRate += +o.averageRate;
      });
      const suburbAverageRate = totalRate / 12;
      settings.type = 'bar';
      settings.data = {};
      settings.data.labels = chartLabel;
      settings.data.datasets = [];
      settings.data.datasets.push({
        label: 'Line Dataset',
        yAxisID: 'A',
        data: chartDataLine,
        borderColor: '#F8D053',
        backgroundColor: 'rgba(254, 246, 221, 0.6)',
        type: 'line'
      });
      settings.data.datasets.push({
        label: 'Bar Dataset',
        yAxisID: 'B',
        data: chartDataBar,
        type: 'bar',
        backgroundColor: '#48B0F7'
      });
      settings.options = {};
      settings.options.tooltips = {};
      settings.options.tooltips = {
        enabled: true,
        mode: 'single',
        callbacks: {}
      };
      settings.options.tooltips.callbacks = {
        label: function(item) {
          return item.datasetIndex === 0
            ? `$${item.yLabel}`
            : `${item.yLabel}%`;
        }
      };
      settings.options.legend = {};
      settings.options.legend.display = false;
      settings.options.elements = {};
      settings.options.elements.point = {};
      settings.options.elements.point.radius = 0;
      settings.options.elements.line = {};
      settings.options.elements.line.borderWidth = 1;
      settings.options.scales = {};
      settings.options.scales.xAxes = [];
      settings.options.scales.xAxes.push({
        display: true,
        gridLines: {
          display: false
        },
        ticks: {
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontStyle: 'bold',
          fontSize: 10
        }
      });
      settings.options.scales.yAxes = [];
      settings.options.scales.yAxes.push({
        display: true,
        id: 'B',
        type: 'linear',
        spanGaps: true,
        gridLines: {
          borderDash: [4],
          drawBorder: false,
          drawTicks: false
        },
        position: 'left',
        ticks: {
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontStyle: 'normal',
          fontSize: 10,
          padding: 10,
          max: 100,
          min: 0,
          stepSize: 20,
          callback: function(value) {
            return `${value}%`;
          }
        }
      });
      settings.options.scales.yAxes.push({
        display: true,
        id: 'A',
        type: 'linear',
        position: 'right',
        spanGaps: true,
        gridLines: {
          borderDash: [4],
          drawBorder: false,
          drawTicks: false
        },
        ticks: this.suburbTicks(suburbAverageRate)
      });
    } else if (CHARTTYPES.PORTFOLIO_VALUE === id) {
      const chartData = data.map(i => i.totalValue);
      const chartLabel = data.map(i => MONTHS[`${i.month}`]);

      const gradient = canvas.createLinearGradient(0, 0, 0, 450);
      gradient.addColorStop(0, 'rgba(72, 176,247, 0.5)');
      gradient.addColorStop(0.5, 'rgba(72, 176,247, 0.25)');
      gradient.addColorStop(1, 'rgba(72, 176,247, 0)');
      settings.type = 'line';
      settings.data = {};
      settings.data.labels = chartLabel;
      settings.data.datasets = [];
      settings.data.datasets.push({
        borderColor: '#48B0F7',
        borderWidth: 2,
        pointBackgroundColor: 'white',
        pointRadius: 4,
        lineTension: 0,
        backgroundColor: gradient,
        data: chartData,
        fill: true
      });
      settings.options = {};
      settings.options.responsive = true;
      settings.options.maintainAspectRatio = true;
      settings.options.animation = {};
      settings.options.animation.easing = 'easeInOutQuad';
      settings.options.animation.duration = 520;
      settings.options.scales = {};
      settings.options.scales.xAxes = [];
      settings.options.scales.xAxes.push({
        gridLines: {
          display: false
        },
        ticks: {
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontStyle: 'normal',
          fontSize: 12,
          fontColor: '#B7B7B7'
        }
      });

      settings.options.scales.yAxes = [];
      settings.options.scales.yAxes.push({
        position: 'right',
        gridLines: {
          borderDash: [4],
          drawBorder: false,
          drawTicks: false
        },
        ticks: {
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontSize: 12,
          padding: 10,
          fontColor: '#B7B7B7',
          beginAtZero: true,
          callback: function(value) {
            return `$${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`;
          }
        }
      });

      settings.options.tooltips = {};
      settings.options.tooltips = {
        enabled: true,
        mode: 'single',
        callbacks: {}
      };
      settings.options.tooltips.callbacks = {
        label: function(item) {
          return `$${item.yLabel.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`;
        }
      };
      settings.options.legend = {};
      settings.options.legend.display = false;
      settings.options.elements = {};
      settings.options.elements.point = {};
      settings.options.elements.point.borderWidth = 2;
      settings.options.elements.line = {};
      settings.options.elements.line.borderWidth = 2;
    } else if (CHARTTYPES.PORTFOLIO_VALUE_MOBILE === id) {
      const chartData = data.map(i => i.totalValue);
      const chartLabel = data.map(i => MONTHS[`${i.month}`]);

      const gradient = canvas.createLinearGradient(0, 0, 0, 450);
      gradient.addColorStop(0, 'rgba(72, 176,247, 0.5)');
      gradient.addColorStop(0.5, 'rgba(72, 176,247, 0.25)');
      gradient.addColorStop(1, 'rgba(72, 176,247, 0)');
      settings.type = 'line';
      settings.data = {};
      settings.data.labels = chartLabel;
      settings.data.datasets = [];
      settings.data.datasets.push({
        borderColor: '#48B0F7',
        borderWidth: 2,
        pointBackgroundColor: 'white',
        pointRadius: 4,
        lineTension: 0,
        backgroundColor: gradient,
        data: chartData,
        fill: true
      });
      settings.options = {};
      settings.options.responsive = true;
      settings.options.maintainAspectRatio = true;
      settings.options.animation = {};
      settings.options.animation.easing = 'easeInOutQuad';
      settings.options.animation.duration = 520;
      settings.options.scales = {};
      settings.options.scales.xAxes = [];
      settings.options.scales.xAxes.push({
        gridLines: {
          display: false
        },
        ticks: {
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontStyle: 'normal',
          fontSize: 12,
          fontColor: '#B7B7B7',
          autoSkip: true,
          maxRotation: 0,
          callback: function(value) {
            return value.substring(0, 3).toUpperCase();
          }
        }
      });

      settings.options.scales.yAxes = [];
      settings.options.scales.yAxes.push({
        position: 'right',
        gridLines: {
          borderDash: [4],
          drawBorder: false,
          drawTicks: false
        },
        scaleLabel: {
          display: true,
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontSize: 10,
          padding: 0,
          fontColor: '#626262',
          fontStyle: 'normal',
          backgroundColor: '#D4EBFA'
        },
        ticks: {
          fontFamily: `'Roboto', Arial, sans-serif`,
          fontSize: 12,
          padding: 10,
          fontColor: '#B7B7B7',
          beginAtZero: true,
          callback: function(value) {
            return `$${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`;
          }
        }
      });

      settings.options.tooltips = {};
      settings.options.tooltips = {
        enabled: true,
        mode: 'single',
        callbacks: {}
      };
      settings.options.tooltips.callbacks = {
        label: function(item) {
          return `$${item.yLabel.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`;
        }
      };
      settings.options.legend = {};
      settings.options.legend.display = false;
      settings.options.elements = {};
      settings.options.elements.point = {};
      settings.options.elements.point.borderWidth = 2;
      settings.options.elements.line = {};
      settings.options.elements.line.borderWidth = 2;
    }

    return settings;
  }

  suburbTicks(rate: number) {
    if (rate >= 0 && rate < 125) {
      return {
        fontFamily: `'Roboto', Arial, sans-serif`,
        fontStyle: 'normal',
        fontSize: 10,
        padding: 10,
        max: 250,
        min: 0,
        stepSize: 50,
        callback: function(value) {
          return `$${value}`;
        }
      };
    } else if (rate >= 125 && rate < 250) {
      return {
        fontFamily: `'Roboto', Arial, sans-serif`,
        fontStyle: 'normal',
        fontSize: 10,
        padding: 10,
        max: 500,
        min: 0,
        stepSize: 100,
        callback: function(value) {
          return `$${value}`;
        }
      };
    } else if (rate >= 250 && rate < 350) {
      return {
        fontFamily: `'Roboto', Arial, sans-serif`,
        fontStyle: 'normal',
        fontSize: 10,
        padding: 10,
        max: 750,
        min: 0,
        stepSize: 150,
        callback: function(value) {
          return `$${value}`;
        }
      };
    } else if (rate >= 350 && rate < 500) {
      return {
        fontFamily: `'Roboto', Arial, sans-serif`,
        fontStyle: 'normal',
        fontSize: 10,
        padding: 10,
        max: 1000,
        min: 0,
        stepSize: 200,
        callback: function(value) {
          return `$${value}`;
        }
      };
    } else if (rate >= 500) {
      return {
        fontFamily: `'Roboto', Arial, sans-serif`,
        fontStyle: 'normal',
        fontSize: 10,
        padding: 10,
        max: 1500,
        min: 0,
        stepSize: 300,
        callback: function(value) {
          return `$${value}`;
        }
      };
    }
  }
}
